import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Archive } from 'phosphor-react';

interface ArquivarProcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nome do processo, so' para o operador confirmar que e' o card certo. */
  titulo?: string;
  isLoading?: boolean;
  onConfirm: (motivo: string) => void;
}

const LIMITE = 500; // igual ao max_length do endpoint

/**
 * Confirmacao de arquivamento com motivo.
 *
 * O motivo e' OPCIONAL de proposito: exigir texto faria o operador digitar
 * qualquer coisa pra passar do modal, o que polui a trilha sem informar nada.
 * Ele vai para a atividade do card (junto de quem e quando) e aparece na lista
 * de arquivados.
 */
export const ArquivarProcessoModal: React.FC<ArquivarProcessoModalProps> = ({
  isOpen,
  onClose,
  titulo,
  isLoading = false,
  onConfirm,
}) => {
  const [motivo, setMotivo] = useState('');
  // Colar um texto maior que o limite era truncado em silencio; avisa.
  const noLimite = motivo.length >= LIMITE;

  // Limpa ao reabrir — senao o motivo do card anterior vaza para o proximo.
  useEffect(() => {
    if (isOpen) setMotivo('');
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
          <Archive size={18} weight="duotone" />
          Arquivar processo
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {titulo && (
            <Text fontSize="13px" fontWeight="700" color="gray.800" mb={1} noOfLines={2}>
              {titulo}
            </Text>
          )}
          <Text fontSize="11px" color="gray.500" mb={4}>
            O processo sai do quadro mas é preservado e pode ser restaurado a qualquer momento.
            Diferente de excluir.
          </Text>

          <FormControl>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700" mb={1}>
              Motivo{' '}
              <Text as="span" fontSize="11px" color="gray.400" fontWeight="400">
                (opcional)
              </Text>
            </FormLabel>
            <Textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value.slice(0, LIMITE))}
              placeholder="Ex.: cliente desistiu, processo duplicado, aberto por engano..."
              rows={3}
              fontSize="13px"
              resize="none"
            />
            <FormHelperText fontSize="11px" color={noLimite ? 'orange.500' : 'gray.400'}>
              {noLimite
                ? `Limite de ${LIMITE} caracteres atingido — o excedente não foi mantido.`
                : 'Fica registrado no histórico do card, junto de quem arquivou e quando.'}
              {motivo.length > 0 && ` · ${motivo.length}/${LIMITE}`}
            </FormHelperText>
          </FormControl>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="red"
            leftIcon={<Archive size={15} weight="bold" />}
            onClick={() => onConfirm(motivo.trim())}
            isLoading={isLoading}
          >
            Arquivar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
